import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppContext } from "../../context/appContext";
import { ImNotification } from "react-icons/im";
import Tooltip from "../Tooltip/Tooltip";
import {
  useGetCatalogueSourceDataForSearchQuery,
  useGetPositionForSearchQuery,
  useGaussSearchDownloadMutation,
  useGetSourceByIdForSearchQuery,
  useGetSourceImagesForSearchQuery,
  useUploadBatchImagesRequestFileMutation,
  useDownloadBatchImageArtefactsMutation,
  useGetSpectralSearchPlotDataQuery
} from "../../redux/searchService";
import CatalogueDataSection from "../CatalogueDataSection/CatalogueDataSection";
import SourceDetail from "../SourceDetail/SourceDetail";
import { useSearchParams } from "react-router-dom";
import {
  BATCH_POSITION,
  CATALOGUE,
  DEC,
  POSITION,
  RA,
  SOURCE,
} from "../../SearchConstants";
import Position from "../Position/Position";
import "./SearchDisplay.scss";
import {
  checkIfObjectHasAllEmptyStringValues,
  isEmptyString,
} from "../../helperFunc";
import Table from "../Table/Table";
import { _ } from "gridjs-react";
import SourceImage from "../SourceImage/SourceImage";
import { RowSelection } from "gridjs-selection";

function SearchDisplay({
  isSourceCallOnNavigate,
  setIsSourceCallOnNavigate,
  batchImagefile = null,
}) {
  const { toggleLoaderVisibility } = useContext(AppContext);
  const { searchFormData, urlRelName } = useSelector((state) => state.router);
  const [sourceDetails, setSourceDetails] = useState({
    sourceID: "",
    pointingName: "",
  });

  const [showCatalogueOrSource, setShowCatalogueOrSource] = useState("");
  const [artefactsDisplayData, setAretefactsDisplayData] = useState({});
  const [displayData, setDisplayData] = useState([]);
  const [positionDisplayData, setPositionDisplayData] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [gaussDownloadPost] = useGaussSearchDownloadMutation();
  const [apiError, setApiError] = useState("");
  const [batchImageData, setBatchImageData] = useState({});
  const [imagesWithLabels, setImagesWithLabels] = useState({});
  const [tableViewData, setTableViewData] = useState([]);
  const [gridTableConfig, setGridTableConfig] = useState({});
  const [shouldUploadBatchFile, setShouldUploadBatchFile] = useState(false);
  const checkIfRADECExists = () => {
    return isEmptyString(searchFormData.ra) || isEmptyString(searchFormData.dec)
      ? false
      : true;
  };
  let { currentData, isFetching, isError, error } =
    useGetCatalogueSourceDataForSearchQuery(
      { ...searchFormData, release_name: urlRelName },
      {
        skip:
          !isEmptyString(sourceDetails.sourceID) ||
          checkIfObjectHasAllEmptyStringValues(searchFormData) ||
          searchFormData.searchBy !== SOURCE ||
          displayData.length !== 0 ||
          isSourceCallOnNavigate ||
          !checkIfRADECExists(),
      }
    );

  let {
    currentData: sourceData,
    isFetching: isSourceFetching,
    isError: isSourceError,
    error: sourceError,
  } = useGetSourceByIdForSearchQuery(
    { sourceID: sourceDetails.sourceID, release_name: urlRelName },
    {
      skip:
        isSourceCallOnNavigate === false ||
        showCatalogueOrSource !== SOURCE ||
        searchFormData.searchBy !== SOURCE ||
        displayData.length !== 0,
    }
  );

  let {
    currentData: artefactsData,
    isFetching: isArtefactsFetching,
    isError: isArtefactsError,
    error: artefactError,
  } = useGetSourceImagesForSearchQuery(
    {
      sourceID: sourceDetails.sourceID,
      pointingName: sourceDetails.pointingName,
      release_name: urlRelName,
    },
    {
      skip:
        checkIfObjectHasAllEmptyStringValues(searchFormData) ||
        sourceDetails.sourceID === "" ||
        sourceDetails.pointingName === "" ||
        searchFormData.searchBy !== SOURCE ||
        Object.keys(artefactsDisplayData).length !== 0,
    }
  );

  let {
    currentData: positionData,
    isFetching: isPositionFetching,
    isError: isPositionError,
    error: positionError,
  } = useGetPositionForSearchQuery(
    {
      ra: searchFormData.ra,
      dec: searchFormData.dec,
      release_name: urlRelName,
    },
    {
      skip:
        checkIfObjectHasAllEmptyStringValues(searchFormData) ||
        searchFormData.searchBy !== POSITION ||
        Object.keys(positionDisplayData).length !== 0 ||
        !checkIfRADECExists(),
    }
  );

  const [uploadBatchImages, { isLoading: isUploadingBatchFiles, isError: isBatchUploadError, error: batchUploadErrorData
  }] =
    useUploadBatchImagesRequestFileMutation();

  const [
    batchImageDownload,
    {
      isSuccess: isBatchImageDownloadSuccessful,
      isLoading: isBatchImageDownloadLoading,
    },
  ] = useDownloadBatchImageArtefactsMutation();

  function findValueForAKey(obj, keyName) {
    let result;

    for (let key in obj) {
      if (key === keyName) {
        return obj[key];
      } else if (typeof obj[key] === "object") {
        result = findValueForAKey(obj[key], keyName);
        if (typeof result !== "undefined") {
          return result;
        }
      }
    }
  }

  const resetData = () => {
    setPositionDisplayData({});
    setAretefactsDisplayData({});
    setDisplayData([]);
    setApiError("");
  };

  function navigateSourceTo(urlId) {
    let [pointingid, sourceid] = urlId?.split("?source_id=") || [];
    window.open(
      `${window.location.href.split("?")[0]
      }?sourceid=${sourceid}&pointingname=${encodeURIComponent(pointingid)}`,
      "_blank",
      "noreferrer"
    );
  }

  const removeQueryParams = () => {
    searchParams.delete(RA);
    searchParams.delete(DEC);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    async function uploadBatchFile() {
      const { data } = await uploadBatchImages({
        release_name: urlRelName,
        radius: searchFormData.cutout_size,
        file: batchImagefile,
      });
      setBatchImageData(data);
    }

    if (!isUploadingBatchFiles && shouldUploadBatchFile) {
      uploadBatchFile();
      setShouldUploadBatchFile(false);
    }
  }, [shouldUploadBatchFile]);


  useEffect(() => {
    setTableViewData([]);
    const sourceId = searchParams.get("sourceid");
    const pointingName = searchParams.get("pointingname");
    resetData();
    setShowCatalogueOrSource("");
    setIsSourceCallOnNavigate(false);
    setSourceDetails({
      sourceID: sourceId || "",
      pointingName: pointingName || "",
    });
    if (sourceId && pointingName) {
      setShowCatalogueOrSource(SOURCE);
      setIsSourceCallOnNavigate(true);
    }
    if (
      batchImagefile &&
      batchImagefile?.size &&
      !isEmptyString(searchFormData.cutout_size)
    ) {
      setShouldUploadBatchFile(true);
    }
  }, [searchFormData]);

  useEffect(() => {
    generateData();
  }, [imagesWithLabels]);

  useEffect(() => {
    if (currentData && !isFetching) {
      setDisplayData(currentData);
    }
  }, [currentData]);

  useEffect(() => {
    if (sourceData && !isSourceFetching) {
      setDisplayData(sourceData);
    }
  }, [sourceData]);

  useEffect(() => {
    if (artefactsData && !isArtefactsFetching) {
      setAretefactsDisplayData(artefactsData);
    }
  }, [artefactsData]);

  useEffect(() => {
    if (positionData && !isPositionFetching) {
      setPositionDisplayData(positionData);
      removeQueryParams();
    }
  }, [positionData]);

  useEffect(() => {
    if (
      isFetching ||
      isPositionFetching ||
      isArtefactsFetching ||
      isSourceFetching ||
      isUploadingBatchFiles ||
      isBatchImageDownloadLoading
    ) {
      toggleLoaderVisibility(true);
    } else {
      toggleLoaderVisibility(false);
    }
  }, [
    isArtefactsFetching,
    isFetching,
    isPositionFetching,
    isSourceFetching,
    isUploadingBatchFiles,
    isBatchImageDownloadLoading
  ]);

  useEffect(() => {
    let errData;
    if (
      (error && isError) ||
      (isArtefactsError && artefactError) ||
      (isPositionError && positionError) ||
      (isSourceError && sourceError) ||
      (isBatchUploadError && batchUploadErrorData)
    ) {
      errData = error || artefactError || positionError || sourceError || batchUploadErrorData;
      setApiError(errData?.data?.message);
      if (errData?.originalStatus === 429 || errData?.status === 429) {
        setApiError("Too many requests. Please try again later");
      }
    }
  }, [isError, isArtefactsError, isPositionError, isSourceError, isBatchUploadError]);

  useEffect(() => {
    if (displayData.length === 0) return;
    let resultType = displayData?.result_type || CATALOGUE;
    setShowCatalogueOrSource(resultType);

    if (resultType === SOURCE && !isSourceCallOnNavigate) {
      setShowCatalogueOrSource(SOURCE);
      let pointingName = findValueForAKey(displayData, "pointing_name");
      let sourceID = findValueForAKey(displayData, "source_id");
      setSourceDetails({
        sourceID,
        pointingName,
      });
    }
  }, [displayData]);

  const statusMessage = batchImageData?.status_message;
  const errorMessage = batchImageData?.errors;
  const columns = batchImageData?.column_names?.map((columnName, index) => {
    return {
      id: columnName,
      name: _(
        <>
          {columnName === "Position"
            ? columnName
            : `${columnName} [Ref Freq: ${parseFloat(
              batchImageData?.reference_freq[columnName]
            ).toFixed(2)}]`}
        </>
      ),
    };
  });

  columns?.unshift({
    id: "Selection",
    name: "Select",
    data: () => true,
    plugin: {
      component: RowSelection,
      props: {
        id: (row) => row.cell(1).data,
        checkboxClassName: "checkbox-rendicion",
      },
    },

    sort: false,
  });

  useEffect(() => {
    if (!batchImageData) return;
    batchImageData?.data?.forEach((positionData) => {
      let { position_artefacts } = positionData;
      let spwObj = {};
      Object.keys(position_artefacts)
        .filter((item) => item !== "Position")
        .forEach((spw) => {
          const image = {
            src: position_artefacts[spw].cutout_png,
            alt: "",
            fits: position_artefacts[spw].cutout_fits,
          };
          spwObj = {
            ...spwObj,
            [spw]: image,
          };
        });
      setImagesWithLabels((prevImageData) => {
        return {
          ...prevImageData,
          [position_artefacts.Position]: spwObj,
        };
      });
    });
  }, [batchImageData]);

  function errorMessages(errors) {
    if (errors?.length === 0) {
      return <></>;
    }

    const errorComponent = errors.map(
      (msg, idx) => <li key={idx}><b>{msg['position']}</b>: {msg['msg']}</li>
    );

    return <Tooltip isPopover={false}>
      <ImNotification
        style={{
          color: "#DC3220",
        }}
        aria-label="notification-icon"
      ></ImNotification>
      <ol>{errorComponent}</ol>
    </Tooltip>;

  }

  function generateData() {
    if (Object.keys(batchImageData).length === 0) return;
    const { data, column_names } = batchImageData;
    let posArtefactsData = data?.map((x) => x?.position_artefacts);
    let keys = column_names?.reduce((acc, val) => {
      acc[val] = " ";
      return acc;
    }, {});
    let posData = posArtefactsData.map((x) => {
      return { ...keys, ...x };
    });
    const data_obj = posData.map((x) => {
      return { ...x, ...transform(x) };
    });
    setTableViewData(data_obj);
  }

  function transform(row) {
    if (Object.keys(imagesWithLabels).length > 0)
      return Object.keys(row)
        .filter((x) => x !== "Selection" && x !== "Position")
        .reduce((acc, val) => {
          if (row[val] && row[val] !== " ") {
            acc[val] = _(
              <>
                <SourceImage data={imagesWithLabels[row["Position"]][val]} />
              </>
            );

          }
          return acc;
        }, {});
  }

  const displayError = () => {
    return <div className="error">{apiError}</div>;
  };

  const downloadFile = (path) => {
    const link = document.createElement('a');
    link.href = path.download_path;
    link.setAttribute('download', `bulk_images.tar.gz`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadZip = async () => {
    const checkboxPlugin = gridTableConfig.config.plugin.get("Selection");
    let { rowIds } = checkboxPlugin.props.store.state;

    let { data: batchImageDownloadFilePath } = await batchImageDownload({
      release_name: urlRelName,
      fitsPaths: {
        fits_file_paths: generateFitsArray(rowIds),
      },
    });
    downloadFile(batchImageDownloadFilePath);
  };

  function generateFitsArray(rowIds) {
    let result = [];
    rowIds.forEach((position) => {
      Object.keys(imagesWithLabels[position]).forEach((spw) => {
        result = [...result, imagesWithLabels[position][spw]["fits"]];
      });
    });
    return result;
  }

  if (apiError) return displayError();
  if (showCatalogueOrSource === CATALOGUE && displayData.length === 0) return;
  if (
    showCatalogueOrSource === SOURCE &&
    (displayData.length === 0 || Object.keys(artefactsDisplayData).length === 0)
  )
    return;
  if (
    searchFormData.searchBy === POSITION &&
    Object.keys(positionDisplayData).length === 0
  )
    return;
  if (
    isFetching ||
    isPositionFetching ||
    isArtefactsFetching ||
    isSourceFetching
  )
    return;
  return (
    <div
      className={`${searchFormData.searchBy === BATCH_POSITION ? "batch-image" : ""
        }`}
    >
      {showCatalogueOrSource === CATALOGUE && (
        <CatalogueDataSection
          data={displayData}
          navigateSourceTo={navigateSourceTo}
          gaussDownloadPost={(sourceIds) =>
            gaussDownloadPost({
              releaseName: urlRelName || null,
              sourceIds: { source_ids: sourceIds },
            })
          }
        />
      )}
      {showCatalogueOrSource === SOURCE && (
        <SourceDetail
          data={displayData?.source_data}
          columnDesc={displayData?.source_view}
          artefactsData={artefactsDisplayData}
          isArtefactsRequestLoading={isArtefactsFetching}
          catalogueMetadataId={urlRelName}
          sourceId={sourceDetails.sourceID}
          getSpectralPlot={useGetSpectralSearchPlotDataQuery}
        />
      )}
      {searchFormData.searchBy === POSITION && (
        <Position
          data={positionDisplayData}
          isPositionLoading={isPositionFetching}
        />
      )}


      {searchFormData.searchBy === BATCH_POSITION && (
        <>
          <div className="batch-status-bar">
            <div className="batch-status-message">
              <span>{statusMessage}</span>
              {errorMessage && errorMessages(errorMessage)}
            </div>
            {tableViewData.length > 0 && (
              <button
                onClick={downloadZip}
                className="bg-green-button batch-img-dwnld"
              >
                Download selected
              </button>
            )}
          </div>
          {tableViewData.length > 0 && (
            <Table
              columns={columns}
              stickyFirstColumn={true}
              data={tableViewData}
              pagination={{
                enabled: true,
                limit: 25,
              }}
              fixedHeader={true}
              sort={true}
              rowClickHandler={null}
              setGridTableConfig={setGridTableConfig}
            />
          )}
        </>
      )}

    </div>
  );
}

export default SearchDisplay;
